import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { Constants } from 'utilities/constants';

export const initialState = {
  [Constants.Module.UNDERWRITE]: {
    queueTxnOpps: null,
    isFetchingQueueTxnOppsData: false,
    queueTxnOppsNeedToRefresh: false,
    queueFilterInput: '',
    queueSortInput: [
      {
        label: 'zipCode',
        order: null
      },
      {
        label: 'beds',
        order: null
      },
      {
        label: 'sqft',
        order: null
      },
      {
        label: 'clear',
        order: null
      },
    ],
    originalMyTasksOrder: [],
    queueDrawerIsOpen: false,
    filteredTasks: [],
    queueBinsState: null,
    showPostAcquisitions: false
  },
  [Constants.Module.POSTACQ]: {
    queueTxnOpps: null,
    isFetchingQueueTxnOppsData: false,
    queueTxnOppsNeedToRefresh: false,
    queueFilterInput: '',
    queueSortInput: [
      {
        label: 'zipCode',
        order: null
      },
      {
        label: 'beds',
        order: null
      },
      {
        label: 'sqft',
        order: null
      },
      {
        label: 'clear',
        order: null
      },
    ],
    originalMyTasksOrder: [],
    queueDrawerIsOpen: false,
    filteredTasks: [],
    queueBinsState: null
  }
}

// create slice will call createAction and createReducer for us internally
const slice = createSlice({
  name: 'queueSlice',
  initialState,
  reducers: {
    setQueueTxnOpps: {
      reducer: (state, action) => {
        const { module, queueTxnOpps } = action.payload;
        state[module].queueTxnOpps = queueTxnOpps;
      },
      prepare: (module, queueTxnOpps) => ({ payload: { module, queueTxnOpps }})
    },
    setIsFetchingQueueTxnOppsData: {
      reducer: (state, action) => {
        const { module, isFetchingQueueTxnOppsData } = action.payload;
        state[module].isFetchingQueueTxnOppsData = isFetchingQueueTxnOppsData;
      },
      prepare: (module, isFetchingQueueTxnOppsData) => ({ payload: { module, isFetchingQueueTxnOppsData }})
    },
    setQueueTxnOppsNeedToRefresh: {
      reducer: (state, action) => {
        const { module, queueTxnOppsNeedToRefresh } = action.payload;
        state[module].queueTxnOppsNeedToRefresh = queueTxnOppsNeedToRefresh;
      },
      prepare: (module, queueTxnOppsNeedToRefresh) => ({ payload: { module, queueTxnOppsNeedToRefresh }})
    },
    setQueueFilterInput: {
      reducer: (state, action) => {
        const { module, queueFilterInput } = action.payload;
        state[module].queueFilterInput = queueFilterInput;
      },
      prepare: (module, queueFilterInput) => ({ payload: { module, queueFilterInput }})
    },
    setShowPostAcquisitions: (state, action) => { // Used by acq module only
        state[Constants.Module.UNDERWRITE].showPostAcquisitions = action.payload;
    },
    // This is the action creator responsible for sending the correct sort input text back (sort ascend/descend)
    setQueueSortInput: {
      reducer: (state, action) => {
        const { module, queueSortInput } = action.payload;
        state[module].queueSortInput = queueSortInput;
      },
      prepare: (module, queueSortInput) => ({ payload: { module, queueSortInput }})
    },
    setOriginalMyTasksOrder: {
      reducer: (state, action) => {
        const { module, originalMyTasksOrder } = action.payload;
        state[module].originalMyTasksOrder = originalMyTasksOrder;
      },
      prepare: (module, originalMyTasksOrder) => ({ payload: { module, originalMyTasksOrder }})
    },
    setQueueDrawerIsOpen: {
      reducer: (state, action) => {
        const { module, queueDrawerIsOpen } = action.payload;
        state[module].queueDrawerIsOpen = queueDrawerIsOpen ?? !state[module].queueDrawerIsOpen;
      },
      prepare: (module, queueDrawerIsOpen) => ({ payload: { module, queueDrawerIsOpen }})
    },
    setFilteredTasks: {
      reducer: (state, action) => {
        const { module, filteredTasks } = action.payload;
        state[module].filteredTasks = filteredTasks;
      },
      prepare: (module, filteredTasks) => ({ payload: { module, filteredTasks }})
    },
    setQueueBinsState: {
      reducer: (state, action) => {
        const { module, queueBinsState } = action.payload;
        state[module].queueBinsState = queueBinsState;
      },
      prepare: (module, queueBinsState) => ({ payload: { module, queueBinsState }})
    },
    // Redux saga actions, no reducers
    fetchQueueTxnOppsData: () => { },
    changeQueueSort: {
      reducer: () => { }, 
      prepare: (module, queueSort) => ({ payload: { module, queueSort }}) // queueSort = { sortInput, quickChange }. This is the menu item input to be sent to the setQueueSortInput Saga
    },
    clearCompletedQueue: () => { },
  }
});

export default slice.reducer;

export const queueActions = slice.actions;

// Memoized selectors
export const queueSelectors = {
  queueTxnOppsSelector: createSelector(
    (state, module) => state.queueState[module].queueTxnOpps,
    queueTxnOpps => queueTxnOpps
  ),
  isFetchingQueueTxnOppsDataSelector: createSelector(
    (state, module) => state.queueState[module].isFetchingQueueTxnOppsData,
    isFetchingQueueTxnOppsData => isFetchingQueueTxnOppsData
  ),
  queueBinsStateSelector: createSelector(
    (state, module) => state.queueState[module].queueBinsState,
    queueBinsState => queueBinsState
  ),
  queueSortInputSelector: createSelector(
    (state, module) => state.queueState[module].queueSortInput,
    queueSortInput => queueSortInput
  ),
  originalMyTasksOrderSelector: createSelector(
    (state, module) => state.queueState[module].originalMyTasksOrder,
    originalMyTasksOrder => originalMyTasksOrder
  ),
  showPostAcquisitionsSelector: createSelector(
    state => state.queueState[Constants.Module.UNDERWRITE].showPostAcquisitions,
    showPostAcquisitions => showPostAcquisitions
  )
}